import { normalizeProductPageUrl } from '@/features/pdp/utils/pdpUrl';
import videoCallIcon from '@/shared/icons/video.svg';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';
import { useState } from 'react';
import viewSimilarIcon from '@/shared/icons/view-similar.svg';
import viewDetailsIcon from '@/shared/icons/view-details.svg';
import { Loader2, Play } from 'lucide-react';
import { cn } from '@/shared/utils/cn';
import { WishlistIcon } from '@/shared/icons/icons';
import discountIcon from '@/shared/icons/discount.svg';
import type { ListingDesignItem } from '../../domain/models/ProductListing';
import { useWishlistToggle } from '@/features/wishlist/presentation/hooks/useWishlistToggle';
import { useVideoCallCart } from '@/features/video-call/presentation/hooks/useVideoCallCart';
import { useIntersectionObserver } from '@/core/hooks/useIntersectionObserver';
import ProductVideoModal from './ProductVideoModal_v2';
import { ProductCardMediaCarousel } from './ProductCardMediaCarousel';
import { RotatingProductDescription } from './RotatingProductDescription';

export interface ProductCardProps {
  /** Product/design item – use ListingDesignItem from any source (browse, search, recently-viewed) */
  product: ListingDesignItem;
  onViewSimilar?: (product: ListingDesignItem) => void;
  similarExpanded?: boolean;
  /** Called before navigating to PDP; pass designId for PLP back-restore anchor (mobile). */
  onBeforeNavigate?: (designId: number) => void;
  /** When true, open PDP in a new tab instead of the same window. */
  openInNewTab?: boolean;
  /** Analytics: called when user clicks the product card or View Details button */
  onProductClick?: (product: ListingDesignItem) => void;
  /** Analytics: eventLogger on wishlist icon tap (before API), browse PLP only in old-react */
  onWishlistClick?: (product: ListingDesignItem, productPosition: number) => void;
  /** Analytics: called when user toggles the wishlist icon (after successful toggle) */
  onWishlistToggle?: (product: ListingDesignItem, action: 'add' | 'remove') => void;
  /** Analytics: called when user clicks the video play button */
  onVideoClick?: (product: ListingDesignItem, productPosition: number) => void;
  /** Analytics: called when user clicks the View Details button specifically */
  onViewDetailsClick?: (product: ListingDesignItem) => void;
  onVideoCallCartAdd?: (product: ListingDesignItem, errorMsg?: string) => void;
  /** When true, hides the Book Video Call button (e.g. on goldcoins PLP). */
  hideVideoCallButton?: boolean;
  /** When true, hides the View Details button completely (for mobile & desktop). */
  hideViewDetailsButton?: boolean;
  /** Automatically advance this card's ordered carousel media while it is visible. */
  autoSlideMedia?: boolean;
  /** 1-based product position in the listing grid (eventLogger). */
  productPosition?: number;
  /** Optional custom badge rendered in the relative image container */
  badge?: React.ReactNode;
  /** Optional custom footer element (e.g. Add to Cart button) */
  footerCta?: React.ReactNode;
  /** Optional extra wrapper class name */
  className?: string;
}

export function ProductCard({ product, onViewSimilar, similarExpanded = false, onBeforeNavigate, openInNewTab, onProductClick,
  onWishlistClick, onWishlistToggle, onVideoClick, onVideoCallCartAdd, onViewDetailsClick,
  productPosition, hideVideoCallButton = false, hideViewDetailsButton = false,
  autoSlideMedia = false,
  badge, footerCta, className }: ProductCardProps) {
  if (import.meta.env.DEV) {
    console.log('[render] ProductCard_v4', { designId: product.designId });
  }
  const [videoOpen, setVideoOpen] = useState(false);
  const { analytics } = useAnalytics();
  const trackViewSimilar = () => analytics?.trackEvent('View Similar CTA Clicked', {
    'Product Id': String(product.designId),
    'Product Name': product.designName,
    'Category Name': product.categoryType ?? '',
  });
  const [heartAnimating, setHeartAnimating] = useState(false);
  const { isWishlisted, toggleWishlist } = useWishlistToggle();
  const { addToVideoCallCart, isLoading: isVideoCallLoading } = useVideoCallCart({ showToast: false });
  const { ref, isIntersecting } = useIntersectionObserver<HTMLDivElement>({ threshold: 0.5 });
  const pdpUrl = normalizeProductPageUrl(product.productPageUrl) ?? undefined;
  const navigateToPdp = () => {
    if (!pdpUrl) return;
    onProductClick?.(product);
    onBeforeNavigate?.(product.designId);
    if (openInNewTab) window.open(pdpUrl, '_blank', 'noopener,noreferrer');
    else window.location.href = pdpUrl;
  };
  const hasSimilarDesigns = product.hasSimilarDesigns === true;
  const cardActionLabel = hasSimilarDesigns ? 'View Similar' : 'View Details';
  const handleCardAction = () => {
    if (hasSimilarDesigns) {
      if (!similarExpanded) trackViewSimilar();
      onViewSimilar?.(product);
      return;
    }
    if (!pdpUrl) return;
    onViewDetailsClick?.(product);
    onBeforeNavigate?.(product.designId);
    if (openInNewTab) window.open(pdpUrl, '_blank', 'noopener,noreferrer');
    else window.location.href = pdpUrl;
  };

  const wishlisted = isWishlisted(product.designId);
  return (
    <article ref={ref} className={cn('relative flex aspect-[2/1] select-none overflow-hidden rounded-xl border border-[#E8E8E8] bg-white text-[#103064]', className)}>
      <div className="relative h-full w-1/2 shrink-0 bg-[#F0F0F0]">
        <div className="absolute inset-0">
          {product.carouselMedia?.length ? (
            <ProductCardMediaCarousel designId={product.designId} designName={product.designName}
              media={product.carouselMedia} isActive={isIntersecting} autoSlide={autoSlideMedia} showProgress onProductClick={navigateToPdp} />
          ) : (
            <button type="button" onClick={navigateToPdp} aria-label={product.designName} className="h-full w-full">
              {product.imageUrl ? <img src={product.imageUrl} alt={product.designName} loading="lazy" className="h-full w-full object-cover" /> : <span>No image</span>}
            </button>
          )}
        </div>
        {badge}
        {product.videoUrl && (
          <button
            type="button"
            aria-label="Play product video"
            className="absolute right-2 top-2 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-white/90 shadow-sm"
            onClick={(e) => {
              e.stopPropagation();
              if (productPosition != null) {
                onVideoClick?.(product, productPosition);
              }
              setVideoOpen(true);
            }}
          >
            <Play className="h-3.5 w-3.5 text-gray-600" aria-hidden />
          </button>
        )}
      </div>
      <div className="relative flex min-w-0 flex-1 flex-col px-3 pb-3 pt-9 max-[399px]:px-2 max-[399px]:pb-2 max-[399px]:pt-2">
        <button type="button" aria-label={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
          className="absolute right-3 top-3 max-[399px]:right-2 max-[399px]:top-2" onClick={async () => {
            setHeartAnimating(true);
            if (productPosition != null) onWishlistClick?.(product, productPosition);
            const action = wishlisted ? 'remove' : 'add';
            if (await toggleWishlist(product.designId)) onWishlistToggle?.(product, action);
          }}>
          <WishlistIcon filled={wishlisted} className={cn('h-5 w-5', wishlisted ? 'text-[#B98599]' : 'text-[#777]', heartAnimating && 'animate-heart-pop')} onAnimationEnd={() => setHeartAnimating(false)} />
        </button>
        <a
          href={pdpUrl}
          onClick={event => {
            event.preventDefault();
            navigateToPdp();
          }}
          className="flex min-w-0 flex-1 cursor-pointer flex-col justify-center text-left"
          aria-label={`View ${product.designName}`}
        >
          <h2 className="truncate text-xs font-medium text-[#AAA] max-[399px]:pr-7">{product.designName}</h2>
          <p className="mt-1 text-base font-semibold text-[#0D2456] max-[399px]:mt-0">{product.discountedPrice}</p>
          <RotatingProductDescription product={product} className="mt-0 flex h-6 items-center overflow-hidden text-[12px] leading-4 text-[#595959] min-[400px]:text-[12px]" />
          {product.discountName && <p className="mt-1 flex min-w-0 items-center gap-1 overflow-hidden rounded-full bg-[#ECFFF3] px-2 py-1 text-[12px] font-medium leading-4 text-[#00852C]">
            <img src={discountIcon} alt="" aria-hidden="true" className="h-4 w-4 shrink-0" /><span className="min-w-0 overflow-hidden whitespace-nowrap">{product.discountName}</span>
          </p>}
        </a>
        {footerCta ? <div className="mt-2 shrink-0 max-[399px]:mt-1">{footerCta}</div> : <div className="mt-2 flex shrink-0 gap-1 max-[399px]:mt-1">
          {!hideViewDetailsButton && <button type="button" aria-label={cardActionLabel}
                  aria-expanded={hasSimilarDesigns ? similarExpanded : undefined}
                  aria-controls={hasSimilarDesigns && similarExpanded ? `similar-designs-${product.designId}` : undefined} onClick={handleCardAction} className={cn("flex min-w-0 flex-1 items-center justify-center gap-1 overflow-hidden rounded-md border border-[#CBD6E8] px-1 py-2 text-[12px] max-[399px]:py-1.5", similarExpanded && "border-[#103064] bg-[#103064] text-white")}>
            <img src={hasSimilarDesigns ? viewSimilarIcon : viewDetailsIcon} alt="" aria-hidden="true" className={cn("h-5 w-5 shrink-0", similarExpanded && "brightness-0 invert")} /><span className="min-w-0 truncate">{cardActionLabel}</span>
          </button>}
          {product.customizationId != null && !hideVideoCallButton && <button type="button" aria-label="Add to video call cart" disabled={isVideoCallLoading}
            className="flex w-10 shrink-0 items-center justify-center rounded-md border border-[#CBD6E8] text-[#16823D] disabled:opacity-50" onClick={async () => {
              const result = await addToVideoCallCart(product.customizationId!);
              onVideoCallCartAdd?.(product, result.success ? undefined : result.message);
            }}>{isVideoCallLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <img src={videoCallIcon} alt="" aria-hidden="true" className="h-5 w-5" />}</button>}
        </div>}
      </div>
      {videoOpen && product.videoUrl && <ProductVideoModal open onClose={() => setVideoOpen(false)} videoUrl={product.videoUrl} videoPosterUrl={product.videoPosterUrl} productName={product.designName} productPageUrl={product.productPageUrl} />}
    </article>
  );
}
